import {
  buildAgreementSignaturePreview,
  getAgreementSectionDisplayTitle,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'

const encoder = new TextEncoder()

const DEFAULT_IMAGE_MIME = 'image/png'

function bytesFromArrayBuffer(buffer) {
  if (buffer instanceof Uint8Array) return buffer
  if (buffer instanceof ArrayBuffer) return new Uint8Array(buffer)
  if (ArrayBuffer.isView(buffer)) {
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
  }
  return null
}

function extensionFromMimeType(mimeType) {
  const normalized = String(mimeType || '').toLowerCase()
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return 'jpg'
  if (normalized === 'image/webp') return 'webp'
  return 'png'
}

function mimeTypeFromName(name, fallback = DEFAULT_IMAGE_MIME) {
  const normalized = String(name || '').toLowerCase()
  if (normalized.endsWith('.jpg') || normalized.endsWith('.jpeg')) return 'image/jpeg'
  if (normalized.endsWith('.webp')) return 'image/webp'
  if (normalized.endsWith('.png')) return 'image/png'
  return fallback
}

function parseDataUrlImage(value) {
  const match = String(value || '').match(/^data:([^;]+);base64,(.+)$/s)
  if (!match || typeof atob !== 'function') return null
  const binary = atob(match[2])
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return {
    bytes,
    mimeType: String(match[1] || '').trim().toLowerCase() || DEFAULT_IMAGE_MIME
  }
}

function readPngDimensions(bytes) {
  if (!(bytes && bytes.length >= 24)) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return {
    width: view.getUint32(16, false),
    height: view.getUint32(20, false)
  }
}

function readJpegDimensions(bytes) {
  if (!(bytes && bytes.length >= 4) || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let offset = 2
  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1
      continue
    }
    const marker = bytes[offset + 1]
    if (marker === 0xd9 || marker === 0xda) break
    if (offset + 4 > bytes.length) break
    const segmentLength = view.getUint16(offset + 2, false)
    if ((marker >= 0xc0 && marker <= 0xc3) || (marker >= 0xc5 && marker <= 0xc7) || (marker >= 0xc9 && marker <= 0xcb) || (marker >= 0xcd && marker <= 0xcf)) {
      return {
        height: view.getUint16(offset + 5, false),
        width: view.getUint16(offset + 7, false)
      }
    }
    offset += 2 + segmentLength
  }
  return null
}

function readAscii(bytes, start, end) {
  let value = ''
  for (let index = start; index < end && index < bytes.length; index += 1) {
    value += String.fromCharCode(bytes[index])
  }
  return value
}

function readUint24LE(bytes, offset) {
  return (bytes[offset] || 0) | ((bytes[offset + 1] || 0) << 8) | ((bytes[offset + 2] || 0) << 16)
}

function readWebpDimensions(bytes) {
  if (!(bytes && bytes.length >= 30)) return null
  if (readAscii(bytes, 0, 4) !== 'RIFF' || readAscii(bytes, 8, 12) !== 'WEBP') return null
  if (readAscii(bytes, 12, 16) === 'VP8X') {
    return {
      width: 1 + readUint24LE(bytes, 24),
      height: 1 + readUint24LE(bytes, 27)
    }
  }
  return null
}

function getImageDimensions(bytes, mimeType) {
  const normalized = String(mimeType || '').toLowerCase()
  if (normalized === 'image/png') return readPngDimensions(bytes)
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return readJpegDimensions(bytes)
  if (normalized === 'image/webp') return readWebpDimensions(bytes)
  return null
}

function fitImageWithinBox(dimensions, maxWidthEmu, maxHeightEmu) {
  const width = dimensions && dimensions.width ? dimensions.width : 1
  const height = dimensions && dimensions.height ? dimensions.height : 1
  const ratio = Math.min(maxWidthEmu / width, maxHeightEmu / height)
  return {
    widthEmu: Math.max(1, Math.round(width * ratio)),
    heightEmu: Math.max(1, Math.round(height * ratio))
  }
}

function imageSourceUrl(source) {
  if (!source) return ''
  if (typeof source === 'string') return source
  if (source && typeof source === 'object') {
    return source.url || source.fileUrl || source.dataUrl || ''
  }
  return ''
}

function imageSourceName(source, fallbackName) {
  if (source && typeof source === 'object') {
    return String(source.name || source.originalName || source.fileName || fallbackName || 'logo.png')
  }
  const url = typeof source === 'string' ? source : ''
  const cleanUrl = String(url || '').split('#')[0].split('?')[0]
  const lastPart = cleanUrl.split('/').filter(Boolean).pop()
  return lastPart || fallbackName || 'logo.png'
}

async function loadDocxImageAsset(source, options = {}) {
  if (!source) return null
  const settings = {
    fallbackName: 'logo.png',
    fallbackMimeType: DEFAULT_IMAGE_MIME,
    maxWidthEmu: 820000,
    maxHeightEmu: 820000,
    ...options
  }

  let bytes = null
  let mimeType = settings.fallbackMimeType
  let fileName = imageSourceName(source, settings.fallbackName)
  const directBytes = source && typeof source === 'object' ? bytesFromArrayBuffer(source.buffer || source.bytes || source.arrayBuffer) : null

  if (directBytes) {
    bytes = directBytes
    mimeType = String(source.type || source.mimeType || settings.fallbackMimeType || DEFAULT_IMAGE_MIME).toLowerCase()
  } else {
    const url = imageSourceUrl(source)
    if (/^data:/i.test(url)) {
      const parsed = parseDataUrlImage(url)
      if (parsed) {
        bytes = parsed.bytes
        mimeType = parsed.mimeType || mimeType
      }
    } else if (url && typeof fetch === 'function') {
      try {
        const response = await fetch(url)
        if (response && response.ok) {
          const buffer = await response.arrayBuffer()
          bytes = bytesFromArrayBuffer(buffer)
          const responseType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
          mimeType = responseType ? responseType.split(';')[0].trim().toLowerCase() : mimeTypeFromName(fileName, mimeType)
        }
      } catch (error) {
        bytes = null
      }
    }
  }

  if (!(bytes && bytes.length)) return null
  mimeType = mimeTypeFromName(fileName, mimeType)
  const dimensions = getImageDimensions(bytes, mimeType)
  const size = fitImageWithinBox(dimensions, settings.maxWidthEmu, settings.maxHeightEmu)
  return {
    bytes,
    fileName: String(fileName || settings.fallbackName || 'logo.png').replace(/[^a-zA-Z0-9._-]+/g, '-') || settings.fallbackName,
    mimeType,
    widthEmu: size.widthEmu,
    heightEmu: size.heightEmu
  }
}

function escapeXml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function toDosDateTime(date = new Date()) {
  const year = Math.max(1980, date.getFullYear())
  const month = date.getMonth() + 1
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const seconds = Math.floor(date.getSeconds() / 2)
  return {
    time: (hours << 11) | (minutes << 5) | seconds,
    date: ((year - 1980) << 9) | (month << 5) | day
  }
}

const crcTable = (() => {
  const table = new Uint32Array(256)
  for (let index = 0; index < 256; index += 1) {
    let value = index
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1)
    }
    table[index] = value >>> 0
  }
  return table
})()

function crc32(bytes) {
  let crc = 0xffffffff
  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

function concatUint8Arrays(chunks) {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const output = new Uint8Array(total)
  let offset = 0
  chunks.forEach((chunk) => {
    output.set(chunk, offset)
    offset += chunk.length
  })
  return output
}

function numberToBytes(value, size) {
  const bytes = new Uint8Array(size)
  const view = new DataView(bytes.buffer)
  if (size === 2) view.setUint16(0, value, true)
  if (size === 4) view.setUint32(0, value, true)
  return bytes
}

function createZip(entries) {
  const localParts = []
  const centralParts = []
  let offset = 0
  const { time, date } = toDosDateTime()

  entries.forEach((entry) => {
    const nameBytes = encoder.encode(entry.name)
    const dataBytes = typeof entry.data === 'string' ? encoder.encode(entry.data) : entry.data
    const checksum = crc32(dataBytes)

    const localHeader = concatUint8Arrays([
      numberToBytes(0x04034b50, 4),
      numberToBytes(20, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 2),
      numberToBytes(time, 2),
      numberToBytes(date, 2),
      numberToBytes(checksum, 4),
      numberToBytes(dataBytes.length, 4),
      numberToBytes(dataBytes.length, 4),
      numberToBytes(nameBytes.length, 2),
      numberToBytes(0, 2),
      nameBytes,
      dataBytes
    ])

    const centralHeader = concatUint8Arrays([
      numberToBytes(0x02014b50, 4),
      numberToBytes(20, 2),
      numberToBytes(20, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 2),
      numberToBytes(time, 2),
      numberToBytes(date, 2),
      numberToBytes(checksum, 4),
      numberToBytes(dataBytes.length, 4),
      numberToBytes(dataBytes.length, 4),
      numberToBytes(nameBytes.length, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 2),
      numberToBytes(0, 4),
      numberToBytes(offset, 4),
      nameBytes
    ])

    localParts.push(localHeader)
    centralParts.push(centralHeader)
    offset += localHeader.length
  })

  const centralDirectory = concatUint8Arrays(centralParts)
  const localDirectory = concatUint8Arrays(localParts)
  const endRecord = concatUint8Arrays([
    numberToBytes(0x06054b50, 4),
    numberToBytes(0, 2),
    numberToBytes(0, 2),
    numberToBytes(entries.length, 2),
    numberToBytes(entries.length, 2),
    numberToBytes(centralDirectory.length, 4),
    numberToBytes(localDirectory.length, 4),
    numberToBytes(0, 2)
  ])

  return concatUint8Arrays([localDirectory, centralDirectory, endRecord])
}

function buildParagraph(text, options = {}) {
  const safeText = escapeXml(text)
  const alignment = options.center
    ? '<w:jc w:val="center"/>'
    : (options.justify ? '<w:jc w:val="both"/>' : '')
  const font = '<w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>'
  const bold = options.bold ? '<w:b/>' : ''
  const size = options.size ? `<w:sz w:val="${options.size}"/><w:szCs w:val="${options.size}"/>` : ''
  return `<w:p>
    <w:pPr>${alignment}</w:pPr>
    <w:r>
      <w:rPr>${font}${bold}${size}</w:rPr>
      <w:t xml:space="preserve">${safeText}</w:t>
    </w:r>
  </w:p>`
}

function buildTableCell(lines, width, options = {}) {
  const cellLines = Array.isArray(lines) && lines.length ? lines : [' ']
  const verticalAlign = options.verticalAlign ? `<w:vAlign w:val="${options.verticalAlign}"/>` : '<w:vAlign w:val="top"/>'
  return `<w:tc>
    <w:tcPr>
      <w:tcW w:w="${width}" w:type="dxa"/>
      ${verticalAlign}
    </w:tcPr>
    ${cellLines.map((line) => buildParagraph(line || ' ')).join('')}
  </w:tc>`
}

function buildAgreementSignatureTable(section, options = {}) {
  const rightHeader = [options.partner, options.country].filter(Boolean).join('\n')
  const signature = buildAgreementSignaturePreview(section, {
    leftHeader: options.leftSignatureHeader || 'Mae Fah Luang University',
    rightHeader: options.rightSignatureHeader || rightHeader
  })
  const cellWidth = 4680
  const borderColor = 'D7DDE8'
  const toLines = (value, fallback) => {
    const normalized = String(value || '').trim() || fallback
    return String(normalized || '').split('\n')
  }

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="0" w:type="auto"/>
      <w:tblBorders>
        <w:top w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
        <w:left w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
        <w:bottom w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
        <w:right w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
        <w:insideH w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
        <w:insideV w:val="single" w:sz="6" w:space="0" w:color="${borderColor}"/>
      </w:tblBorders>
    </w:tblPr>
    <w:tblGrid>
      <w:gridCol w:w="${cellWidth}"/>
      <w:gridCol w:w="${cellWidth}"/>
    </w:tblGrid>
    <w:tr>
      ${buildTableCell(toLines(signature.left.header, '........................................'), cellWidth)}
      ${buildTableCell(toLines(signature.right.header, '........................................'), cellWidth)}
    </w:tr>
    <w:tr>
      ${buildTableCell([' ', ' ', '........................................'], cellWidth, { verticalAlign: 'bottom' })}
      ${buildTableCell([' ', ' ', '........................................'], cellWidth, { verticalAlign: 'bottom' })}
    </w:tr>
    <w:tr>
      ${buildTableCell(toLines(signature.left.name, '(Name) ........................................'), cellWidth)}
      ${buildTableCell(toLines(signature.right.name, '(Name) ........................................'), cellWidth)}
    </w:tr>
    <w:tr>
      ${buildTableCell(toLines(signature.left.position, '(Position) ........................................'), cellWidth)}
      ${buildTableCell(toLines(signature.right.position, '(Position) ........................................'), cellWidth)}
    </w:tr>
    <w:tr>
      ${buildTableCell(toLines(signature.left.date, 'Date:'), cellWidth)}
      ${buildTableCell(toLines(signature.right.date, 'Date:'), cellWidth)}
    </w:tr>
	  </w:tbl>`
}

const EMBEDDED_SIGNATURE_NAME_LINE_PATTERN = /^(\(name\)|name\b|ชื่อ\s*:?\s*|mr\.?|mrs\.?|ms\.?|miss\b|dr\.?|prof\.?|asst\.?\s*prof\.?|assoc\.?\s*prof\.?|assistant professor\b|associate professor\b|professor\b)/i
const EMBEDDED_SIGNATURE_POSITION_LINE_PATTERN = /^(\(position\)|position\b|ตำแหน่ง\s*:?\s*|president\b|dean\b|vice\b|director\b|rector\b|chair\b|coordinator\b|chancellor\b)/i
const EMBEDDED_SIGNATURE_DATE_LINE_PATTERN = /^(date\b|วันที่\s*:?\s*)/i
const EMBEDDED_SIGNATURE_INSTITUTION_LINE_PATTERN = /\b(university|college|faculty|department|institute|school|mae fah luang|thailand|kingdom)\b|มหาวิทยาลัย|สถาบัน/i
const EMBEDDED_SIGNATURE_PHRASE_PATTERN = /\b(signed\s+(for|on behalf)|for and on behalf|in witness whereof|signatories|signature block)\b|ส่วนลงนาม|ลายมือชื่อ|ผู้ลงนาม/i
const EMBEDDED_SIGNATURE_DOTTED_LINE_PATTERN = /[._-]{6,}/

function buildDefaultAgreementSignatureContent(options = {}) {
  const rightHeader = [options.partner, options.country].filter(Boolean).join(', ') || 'Partner Institution'
  return [
    `Mae Fah Luang University || ${rightHeader}`,
    '(Name) ........................................ || (Name) ........................................',
    '(Position) ........................................ || (Position) ........................................',
    'Date: || Date:'
  ].join('\n')
}

function isPrimaryEmbeddedSignatureLine(line) {
  const normalized = String(line || '').trim()
  return normalized.includes('||')
    || EMBEDDED_SIGNATURE_NAME_LINE_PATTERN.test(normalized)
    || EMBEDDED_SIGNATURE_POSITION_LINE_PATTERN.test(normalized)
    || EMBEDDED_SIGNATURE_DATE_LINE_PATTERN.test(normalized)
    || EMBEDDED_SIGNATURE_PHRASE_PATTERN.test(normalized)
    || EMBEDDED_SIGNATURE_DOTTED_LINE_PATTERN.test(normalized)
}

function isAdjacentEmbeddedSignatureLine(line) {
  const normalized = String(line || '').trim()
  if (!normalized) return false
  if (/[-•]\s+\S/.test(normalized)) return false
  if (/[.!?]$/.test(normalized) && normalized.split(/\s+/).length > 4) return false
  return normalized.length <= 90 || EMBEDDED_SIGNATURE_INSTITUTION_LINE_PATTERN.test(normalized)
}

function splitEmbeddedSignatureContent(value) {
  const lines = String(value || '').replace(/\r\n/g, '\n').split('\n')
  const trimmed = lines.map((line) => String(line || '').trim())
  const primaryIndexes = trimmed
    .map((line, index) => ({ line, index }))
    .filter((item) => item.line && isPrimaryEmbeddedSignatureLine(item.line))
    .map((item) => item.index)
  const hasTwoColumnLine = trimmed.some((line) => line.includes('||'))

  if (!hasTwoColumnLine && primaryIndexes.length < 2) {
    return {
      bodyLines: lines,
      signatureContent: ''
    }
  }

  const signatureIndexes = new Set(primaryIndexes)
  primaryIndexes.forEach((index) => {
    const previous = index - 1
    const next = index + 1
    if (previous >= 0 && isAdjacentEmbeddedSignatureLine(trimmed[previous])) signatureIndexes.add(previous)
    if (next < trimmed.length && isAdjacentEmbeddedSignatureLine(trimmed[next])) signatureIndexes.add(next)
  })

  const signatureLines = []
  const bodyLines = []
  lines.forEach((line, index) => {
    const normalized = String(line || '').trim()
    if (signatureIndexes.has(index)) {
      if (normalized) signatureLines.push(normalized)
    } else {
      bodyLines.push(line)
    }
  })

  return {
    bodyLines,
    signatureContent: signatureLines.join('\n')
  }
}

function registerDocxImage(imageAssets, asset, prefix) {
  const index = imageAssets.length + 1
  const extension = extensionFromMimeType(asset.mimeType)
  const fileName = `${String(prefix || 'doc-image').replace(/[^a-zA-Z0-9_-]+/g, '-')}-${index}.${extension}`
  const imageAsset = {
    ...asset,
    relId: `rId${index}`,
    docPrId: index,
    fileName
  }
  imageAssets.push(imageAsset)
  return imageAsset
}

function buildImageParagraph(relationshipId, widthEmu, heightEmu, docPrId, name) {
  const safeName = escapeXml(name || 'Document Logo')
  return `<w:p>
    <w:pPr><w:jc w:val="center"/></w:pPr>
    <w:r>
      <w:drawing>
        <wp:inline distT="0" distB="0" distL="0" distR="0">
          <wp:extent cx="${widthEmu}" cy="${heightEmu}"/>
          <wp:effectExtent l="0" t="0" r="0" b="0"/>
          <wp:docPr id="${docPrId}" name="${safeName}"/>
          <wp:cNvGraphicFramePr>
            <a:graphicFrameLocks noChangeAspect="1"/>
          </wp:cNvGraphicFramePr>
          <a:graphic>
            <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
              <pic:pic>
                <pic:nvPicPr>
                  <pic:cNvPr id="${docPrId}" name="${safeName}"/>
                  <pic:cNvPicPr/>
                </pic:nvPicPr>
                <pic:blipFill>
                  <a:blip r:embed="${relationshipId}"/>
                  <a:stretch><a:fillRect/></a:stretch>
                </pic:blipFill>
                <pic:spPr>
                  <a:xfrm>
                    <a:off x="0" y="0"/>
                    <a:ext cx="${widthEmu}" cy="${heightEmu}"/>
                  </a:xfrm>
                  <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
                </pic:spPr>
              </pic:pic>
            </a:graphicData>
          </a:graphic>
        </wp:inline>
      </w:drawing>
    </w:r>
  </w:p>`
}

function buildLogoTableCell(content, width) {
  return `<w:tc>
    <w:tcPr>
      <w:tcW w:w="${width}" w:type="dxa"/>
      <w:vAlign w:val="center"/>
    </w:tcPr>
    ${content || buildParagraph(' ')}
  </w:tc>`
}

function buildLogoPlaceholderCell(label, width) {
  const text = String(label || '').trim()
  return buildLogoTableCell(text ? buildParagraph(text, { center: true, size: 20 }) : buildParagraph(' '), width)
}

function normalizeCoverTitleLines(value) {
  if (Array.isArray(value)) {
    return value
      .map((line) => String(line || '').trim())
      .filter(Boolean)
  }
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function isAgreementTitleBlockSection(section) {
  const key = String(section && section.key ? section.key : '').trim().toLowerCase()
  return key === 'title-block'
}

function buildDefaultCoverTitleLines(title, options = {}) {
  const activityText = `${options.activityType || ''} ${title || ''}`.toLowerCase()
  const isStudentExchange = activityText.includes('exchange') && activityText.includes('student')
  if (isStudentExchange) {
    const partnerLine = [options.partner, options.country].filter(Boolean).join(', ') || 'Partner Institution'
    return [
      'Agreement on Student Exchange',
      'between',
      'Mae Fah Luang University, Kingdom of Thailand',
      'and',
      partnerLine
    ]
  }
  return title ? [title] : []
}

function getAgreementCoverTitleLines(title, sections, options = {}) {
  if (options.includeCoverTitle === false) return []
  const overrideLines = normalizeCoverTitleLines(options.coverTitleLines)
  if (overrideLines.length) return overrideLines
  const titleBlock = (Array.isArray(sections) ? sections : []).find(isAgreementTitleBlockSection)
  const contentLines = normalizeCoverTitleLines(titleBlock && titleBlock.content)
  return contentLines.length ? contentLines : buildDefaultCoverTitleLines(title, options)
}

function buildCoverTitleContent(lines) {
  const titleLines = Array.isArray(lines) ? lines.filter((line) => String(line || '').trim()) : []
  if (!titleLines.length) return buildParagraph(' ')
  return titleLines.map((line, index) => buildParagraph(line, {
    bold: index === 0,
    center: true,
    size: index === 0 ? 30 : 24
  })).join('')
}

async function buildLogoHeader(imageAssets, options = {}) {
  const includeMfuLogo = options.includeMfuLogo !== false
  const mfuLogo = includeMfuLogo
    ? await loadDocxImageAsset(options.mfuLogo, {
      fallbackName: 'mfu-logo.png',
      fallbackMimeType: DEFAULT_IMAGE_MIME,
      maxWidthEmu: 780000,
      maxHeightEmu: 980000
    })
    : null
  const partnerLogo = await loadDocxImageAsset(options.partnerLogo, {
    fallbackName: 'partner-logo.png',
    fallbackMimeType: DEFAULT_IMAGE_MIME,
    maxWidthEmu: 820000,
    maxHeightEmu: 820000
  })

  if (!mfuLogo && !partnerLogo) return ''

  const sideCellWidth = 2200
  const centerCellWidth = 4960
  const left = mfuLogo ? registerDocxImage(imageAssets, mfuLogo, 'mfu-logo') : null
  const right = partnerLogo ? registerDocxImage(imageAssets, partnerLogo, 'partner-logo') : null
  const partnerLabel = [options.partner, options.country].filter(Boolean).join(', ')

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="0" w:type="auto"/>
      <w:tblBorders>
        <w:top w:val="nil"/>
        <w:left w:val="nil"/>
        <w:bottom w:val="nil"/>
        <w:right w:val="nil"/>
        <w:insideH w:val="nil"/>
        <w:insideV w:val="nil"/>
      </w:tblBorders>
    </w:tblPr>
    <w:tblGrid>
      <w:gridCol w:w="${sideCellWidth}"/>
      <w:gridCol w:w="${centerCellWidth}"/>
      <w:gridCol w:w="${sideCellWidth}"/>
    </w:tblGrid>
    <w:tr>
      ${left ? buildLogoTableCell(buildImageParagraph(left.relId, left.widthEmu, left.heightEmu, left.docPrId, left.fileName), sideCellWidth) : buildLogoPlaceholderCell('Mae Fah Luang University', sideCellWidth)}
      ${buildLogoPlaceholderCell('', centerCellWidth)}
      ${right ? buildLogoTableCell(buildImageParagraph(right.relId, right.widthEmu, right.heightEmu, right.docPrId, right.fileName), sideCellWidth) : buildLogoPlaceholderCell(partnerLabel, sideCellWidth)}
    </w:tr>
  </w:tbl>`
}

function buildDocumentXml(title, bodyText) {
  const paragraphs = []
  if (title) paragraphs.push(buildParagraph(title, { bold: true, center: true, size: 32 }))
  const bodyLines = String(bodyText || '').split(/\r?\n/)
  if (bodyLines.length) {
    bodyLines.forEach((line) => {
      paragraphs.push(buildParagraph(line || ' '))
    })
  } else {
    paragraphs.push(buildParagraph(' '))
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"
  xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math"
  xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"
  xmlns:wp14="http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:w10="urn:schemas-microsoft-com:office:word"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"
  xmlns:wpg="http://schemas.microsoft.com/office/word/2010/wordprocessingGroup"
  xmlns:wpi="http://schemas.microsoft.com/office/word/2010/wordprocessingInk"
  xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml"
  xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"
  mc:Ignorable="w14 wp14">
  <w:body>
    ${paragraphs.join('')}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="1152" w:right="1152" w:bottom="1152" w:left="1152" w:header="708" w:footer="708" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`
}

function buildDocumentXmlFromBlocks(blocks) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"
  xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math"
  xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"
  xmlns:wp14="http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:w10="urn:schemas-microsoft-com:office:word"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"
  xmlns:wpg="http://schemas.microsoft.com/office/word/2010/wordprocessingGroup"
  xmlns:wpi="http://schemas.microsoft.com/office/word/2010/wordprocessingInk"
  xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml"
  xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape"
  mc:Ignorable="w14 wp14">
  <w:body>
    ${blocks.join('')}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="1152" w:right="1152" w:bottom="1152" w:left="1152" w:header="708" w:footer="708" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`
}

function buildCoreXml(title) {
  const now = new Date().toISOString()
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
  xmlns:dc="http://purl.org/dc/elements/1.1/"
  xmlns:dcterms="http://purl.org/dc/terms/"
  xmlns:dcmitype="http://purl.org/dc/dcmitype/"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>${escapeXml(title || 'Translated Document')}</dc:title>
  <dc:creator>MFU MOU Management System</dc:creator>
  <cp:lastModifiedBy>MFU MOU Management System</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`
}

export function buildDocxBlob(title, bodyText) {
  const entries = [
    {
      name: '[Content_Types].xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`
    },
    {
      name: '_rels/.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`
    },
    {
      name: 'docProps/app.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"
  xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>MFU MOU Management System</Application>
</Properties>`
    },
    {
      name: 'docProps/core.xml',
      data: buildCoreXml(title)
    },
    {
      name: 'word/_rels/document.xml.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`
    },
    {
      name: 'word/styles.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:qFormat/>
    <w:rPr>
      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
      <w:sz w:val="24"/>
      <w:szCs w:val="24"/>
    </w:rPr>
  </w:style>
</w:styles>`
    },
    {
      name: 'word/document.xml',
      data: buildDocumentXml(title, bodyText)
    }
  ]

  const zipBytes = createZip(entries)
  return new Blob([zipBytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  })
}

export async function buildAgreementDocxBlob(title, sections, options = {}) {
  const normalizedSections = normalizeAgreementSections(sections)
  const imageAssets = []
  const blocks = []
  const coverTitleLines = getAgreementCoverTitleLines(title, normalizedSections, options)
  const logoHeader = await buildLogoHeader(imageAssets, {
    ...options,
    coverTitleLines
  })

  if (logoHeader) {
    blocks.push(logoHeader)
    if (coverTitleLines.length) {
      blocks.push(buildCoverTitleContent(coverTitleLines))
    }
    blocks.push(buildParagraph(' '))
  } else if (coverTitleLines.length) {
    blocks.push(buildCoverTitleContent(coverTitleLines))
    blocks.push(buildParagraph(' '))
  }

  if (!coverTitleLines.length && options.includeDocumentTitle !== false && title) {
    blocks.push(buildParagraph(title, { bold: true, center: true, size: 32 }))
    blocks.push(buildParagraph(' '))
  }

  let hasSignatureSection = false

  normalizedSections
    .filter((section) => !isAgreementTitleBlockSection(section))
    .forEach((section) => {
      const displayTitle = getAgreementSectionDisplayTitle(section)
      if (options.includeSectionTitles !== false && displayTitle) {
        blocks.push(buildParagraph(displayTitle, { bold: true }))
      }

      if (isSignatureAgreementSection(section)) {
        hasSignatureSection = true
        blocks.push(buildAgreementSignatureTable(section, options))
      } else {
        const centered = isCenteredAgreementSection(section, options.activityType)
        const embedded = splitEmbeddedSignatureContent(section && section.content ? section.content : '')
        const lines = embedded.bodyLines
        if (lines.length) {
          lines.forEach((line) => {
            blocks.push(buildParagraph(line || ' ', { center: centered, justify: !centered }))
          })
        } else {
          blocks.push(buildParagraph(' ', { center: centered }))
        }
        if (embedded.signatureContent) {
          hasSignatureSection = true
          blocks.push(buildParagraph(' '))
          blocks.push(buildAgreementSignatureTable({
            key: 'signature-block',
            title: 'Signature Block',
            content: embedded.signatureContent
          }, options))
        }
      }

      blocks.push(buildParagraph(' '))
    })

  if (!hasSignatureSection && normalizedSections.length) {
    blocks.push(buildAgreementSignatureTable({
      key: 'signature-block',
      title: 'Signature Block',
      content: buildDefaultAgreementSignatureContent(options)
    }, options))
    blocks.push(buildParagraph(' '))
  }

  if (!blocks.length) {
    blocks.push(buildParagraph(' '))
  }

  const imageContentTypes = Array.from(new Set(imageAssets.map((item) => item.mimeType).filter(Boolean)))
    .map((mimeType) => `  <Default Extension="${extensionFromMimeType(mimeType)}" ContentType="${mimeType}"/>`)
    .join('\n')
  const imageRelationships = imageAssets.map((item) => `
  <Relationship Id="${item.relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/${item.fileName}"/>`).join('')
  const entries = [
    {
      name: '[Content_Types].xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
${imageContentTypes ? `${imageContentTypes}\n` : ''}  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`
    },
    {
      name: '_rels/.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`
    },
    {
      name: 'docProps/app.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"
  xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>MFU MOU Management System</Application>
</Properties>`
    },
    {
      name: 'docProps/core.xml',
      data: buildCoreXml(title)
    },
    {
      name: 'word/_rels/document.xml.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${imageRelationships}
</Relationships>`
    },
    {
      name: 'word/styles.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:qFormat/>
    <w:rPr>
      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
      <w:sz w:val="24"/>
      <w:szCs w:val="24"/>
    </w:rPr>
  </w:style>
</w:styles>`
    },
    {
      name: 'word/document.xml',
      data: buildDocumentXmlFromBlocks(blocks)
    }
  ]
  imageAssets.forEach((item) => {
    entries.push({
      name: `word/media/${item.fileName}`,
      data: item.bytes
    })
  })

  const zipBytes = createZip(entries)
  return new Blob([zipBytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  })
}
